<script lang="ts">
  /**
   * Картка одного коментаря: аватарка, ім'я, дата, текст і ряд дій.
   *
   * Текст рендериться як {comment.text}, і це принципово: коментарі — перший
   * user-generated контент у застосунку, тож {@html} чи renderMarkdown над ним
   * були б прямою XSS-дірою. Переноси рядків дає white-space: pre-wrap.
   *
   * «Поскаржитися» і «Видалити» сховані під «…»: ряд дій на 360px уже несе
   * лайк із лічильником і «Відповісти», а четверта кнопка і ламає верстку в
   * один рядок, і робить деструктивну дію надто доступною для випадкового тапа.
   */
  import Avatar from './Avatar.svelte';
  // Самоімпорт замість застарілого <svelte:self>. Рекурсія тут рівно на один
  // виток: у відповіді replies завжди порожній (інваріант тримає API).
  import Self from './CommentItem.svelte';
  import Icon from './Icon.svelte';
  import { formatDate } from '../lib/format';
  import type { CommentDTO } from '../lib/types';

  interface Props {
    comment: CommentDTO;
    isReply?: boolean;
    canWrite?: boolean;
    onLike: (c: CommentDTO) => void;
    onReply: (c: CommentDTO) => void;
    onDelete: (c: CommentDTO) => void;
    onReport: (c: CommentDTO) => void;
  }
  let {
    comment,
    isReply = false,
    canWrite = true,
    onLike,
    onReply,
    onDelete,
    onReport,
  }: Props = $props();

  let menuOpen = $state(false);

  const hasMenu = $derived(comment.canDelete || comment.canReport);

  function closeMenu(): void {
    menuOpen = false;
  }

  // Клік поза меню і Esc закривають його. Слухачі вішаємо лише поки меню
  // відкрите — інакше кожна картка у стрічці тримала б по два глобальні хендлери.
  $effect(() => {
    if (!menuOpen) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') closeMenu();
    };
    // Клік по самій кнопці «…» і по вмісту меню зупиняється через
    // stopPropagation, тож сюди долітають лише кліки «десь інде».
    document.addEventListener('click', closeMenu);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('click', closeMenu);
      document.removeEventListener('keydown', onKey);
    };
  });
</script>

<div class="comment" class:reply={isReply}>
  <Avatar photo={comment.author.photoUrl} name={comment.author.name} size={isReply ? 28 : 36} />

  <div class="body">
    <div class="head">
      <span class="name">{comment.author.name}</span>
      {#if comment.author.isAdmin}<span class="team">Команда</span>{/if}
      <span class="date">{formatDate(comment.createdAt)}</span>

      {#if hasMenu}
        <div class="menu-wrap">
          <button
            type="button"
            class="menu-btn"
            aria-label="Дії з коментарем"
            aria-expanded={menuOpen}
            onclick={(e) => {
              e.stopPropagation();
              menuOpen = !menuOpen;
            }}
          >
            <Icon name="ellipsis" size="xs" />
          </button>

          {#if menuOpen}
            <!-- svelte-ignore a11y_click_events_have_key_events a11y_no_static_element_interactions -->
            <div class="menu" role="menu" tabindex="-1" onclick={(e) => e.stopPropagation()}>
              {#if comment.canReport}
                {#if comment.reportedByMe}
                  <span class="menu-item done"><Icon name="flag" size="xs" /> Скаргу надіслано</span>
                {:else}
                  <button
                    type="button"
                    class="menu-item"
                    role="menuitem"
                    onclick={() => {
                      closeMenu();
                      onReport(comment);
                    }}
                  >
                    <Icon name="flag" size="xs" /> Поскаржитися
                  </button>
                {/if}
              {/if}
              {#if comment.canDelete}
                <button
                  type="button"
                  class="menu-item danger"
                  role="menuitem"
                  onclick={() => {
                    closeMenu();
                    onDelete(comment);
                  }}
                >
                  <Icon name="trash-2" size="xs" /> Видалити
                </button>
              {/if}
            </div>
          {/if}
        </div>
      {/if}
    </div>

    <div class="text">{comment.text}</div>

    <div class="actions">
      <button
        type="button"
        class="action like"
        class:active={comment.likedByMe}
        disabled={!comment.canLike}
        aria-label={comment.likedByMe ? 'Прибрати лайк' : 'Лайкнути коментар'}
        onclick={() => onLike(comment)}
      >
        <Icon name="heart" size="xs" filled={comment.likedByMe} />
        {#if comment.likeCount > 0}<span class="count">{comment.likeCount}</span>{/if}
        <span>Лайк</span>
      </button>

      {#if !isReply && canWrite}
        <button type="button" class="action" onclick={() => onReply(comment)}>Відповісти</button>
      {/if}
    </div>

    {#if comment.replies.length > 0}
      <div class="replies">
        {#each comment.replies as reply (reply.id)}
          <Self
            comment={reply}
            isReply
            {canWrite}
            {onLike}
            {onReply}
            {onDelete}
            {onReport}
          />
        {/each}
      </div>
    {/if}
  </div>
</div>

<style>
  .comment {
    display: flex;
    gap: 10px;
    padding: 12px 0;
  }
  .comment.reply {
    padding: 10px 0 0;
  }
  .body {
    flex: 1;
    min-width: 0;
  }
  .head {
    display: flex;
    align-items: center;
    gap: 8px;
  }
  .name {
    font-size: 13px;
    font-weight: 600;
    color: var(--text);
  }
  .team {
    font-size: 10px;
    font-weight: 600;
    padding: 1px 6px;
    border-radius: 999px;
    background: var(--sage-light);
    color: var(--sage-ink);
  }
  .date {
    font-size: 11px;
    color: var(--faint);
  }
  .menu-wrap {
    position: relative;
    margin-left: auto;
  }
  .menu-btn {
    background: none;
    border: none;
    padding: 4px;
    cursor: pointer;
    color: var(--text-muted);
    display: inline-flex;
    border-radius: 6px;
  }
  .menu {
    position: absolute;
    right: 0;
    top: 100%;
    z-index: 20;
    min-width: 176px;
    background: var(--bg2);
    border: 1px solid var(--border);
    border-radius: 10px;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.18);
    padding: 4px;
    display: flex;
    flex-direction: column;
  }
  .menu-item {
    display: flex;
    align-items: center;
    gap: 8px;
    background: none;
    border: none;
    text-align: left;
    padding: 9px 10px;
    border-radius: 7px;
    font-size: 13px;
    font-family: inherit;
    color: var(--text);
    cursor: pointer;
  }
  .menu-item:hover {
    background: var(--sand-soft);
  }
  .menu-item.danger {
    color: var(--status-inactive);
  }
  .menu-item.done {
    color: var(--faint);
    cursor: default;
  }
  .text {
    margin-top: 4px;
    font-size: 14px;
    line-height: 1.5;
    color: var(--text);
    /* pre-wrap зберігає переноси рядків автора; anywhere рятує від «стіни» з
       3000 символів без жодного пробілу. */
    white-space: pre-wrap;
    overflow-wrap: anywhere;
  }
  .actions {
    display: flex;
    align-items: center;
    gap: 14px;
    margin-top: 8px;
  }
  .action {
    background: none;
    border: none;
    padding: 0;
    font-family: inherit;
    font-size: 12px;
    color: var(--text-muted);
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: 5px;
  }
  .action:disabled {
    cursor: default;
    opacity: 0.55;
  }
  .action.like.active {
    color: var(--heart);
  }
  .count {
    font-variant-numeric: tabular-nums;
  }
  .replies {
    margin-top: 4px;
    padding-left: 12px;
    border-left: 1px solid var(--border);
  }
</style>
